import React, { forwardRef, type ReactNode } from 'react'
import { Pressable, View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { AdditionalText } from '../additionaltext/AdditionalText'
import { Label } from '../label/Label'
import { dropdownContract } from './dropdown.contract'

const VARIANTS = dropdownContract.props.variant.values

export type DropdownVariant = (typeof VARIANTS)[number]

export const DROPDOWN_TEXT = variantForStyleId(dropdownContract.textStyleIds[0])

type ContractCell = {
    container: string
    label: string
    icon: string
    box: string
    parts: Record<string, string>
}

const cellFor = (variant: DropdownVariant): ContractCell => {
    const key = `variant=${variant}`
    const found = (dropdownContract.variants as Record<string, ContractCell>)[key]
    if (!found) {
        throw new Error(`aos-ds Dropdown: the Figma contract has no "${key}"`)
    }
    return found
}

export type DropdownProps = {
    /**
     * Figma's six. `opened` and `focus` are drawn states rather than things
     * this component decides: the panel is `DropdownList`'s, and a trigger that
     * decided when it was open would own state the caller already has.
     */
    variant?: DropdownVariant
    /** Figma's `showLabel`, the `Label` above the field. */
    label?: string
    required?: boolean
    /** Figma's `helpText` — what the closed trigger reads when nothing is picked. */
    placeholder?: string
    value?: string
    /** Figma's `showAdditionalText`, the hint or error under the field. */
    additionalText?: string
    /** Figma's `leftIcon` and `chevronIcon`, both INSTANCE_SWAP — the caller's glyphs. */
    iconLeft?: ReactNode
    chevron?: ReactNode
    onPress?: () => void
    className?: string
    testID?: string
}

type DropdownRef = React.ComponentRef<typeof Pressable>

/**
 * The closed trigger: a `Label`, a field, and an `AdditionalText` under it.
 * Same three-part shape as `Input`, and the same reason for two class sets —
 * Figma paints the field on an `input` child inside a stack that also holds the
 * label and the hint, so one element wearing both renders the label inside the
 * field.
 *
 * Hover is not a prop. The contract folds Figma's `hover` axis into
 * `group-hover/dropdown:` on the field, so the pointer is the browser's
 * business; `container` carries the group name for four of the six variants and
 * omits it for `disabled` and `opened`, which the file draws with no hover of
 * their own.
 *
 * The disabled opacity is in `container`, not in `disabled`. Figma spells
 * `disabled` as one value of the `variant` axis rather than as a state, so it
 * is a cell — the distinction `TextAreaInput` shipped wrong.
 */
export const Dropdown = forwardRef<DropdownRef, DropdownProps>(
    (
        {
            variant = 'not-filled',
            label,
            required,
            placeholder,
            value,
            additionalText,
            iconLeft,
            chevron,
            onPress,
            className,
            testID,
        },
        ref,
    ) => {
        const cell = cellFor(variant)
        const disabled = variant === 'disabled'
        return (
            <View
                className={twMerge('flex-col', cell.container, className)}
                testID={testID ?? `dropdown-${variant}`}
            >
                {label ? <Label required={required}>{label}</Label> : null}
                <Pressable
                    ref={ref}
                    disabled={disabled}
                    onPress={onPress}
                    className={twMerge('w-full flex-row', cell.parts.input)}
                    testID='dropdown-trigger'
                >
                    <View className={twMerge('flex-1 flex-row', cell.parts.content)}>
                        {iconLeft}
                        <Typography
                            variant={DROPDOWN_TEXT}
                            className={twMerge('flex-1', cell.parts['Help text'])}
                            numberOfLines={1}
                        >
                            {value ?? placeholder}
                        </Typography>
                    </View>
                    {chevron ? (
                        <View className={twMerge('flex-row', cell.parts['right-icons'])}>
                            {chevron}
                        </View>
                    ) : null}
                </Pressable>
                {additionalText ? (
                    <AdditionalText variant={variant === 'error' ? 'error' : 'default'}>
                        {additionalText}
                    </AdditionalText>
                ) : null}
            </View>
        )
    },
)

Dropdown.displayName = 'Dropdown'

export { VARIANTS as DROPDOWN_VARIANTS }
